import { defineComponent as _defineComponent } from 'vue'
import { onBeforeUnmount, ref, computed } from 'vue';
import store from "@/bootstrap/store";
import {TranslateResult} from 'vue-i18n'
import i18n from '@/plugins/i18n';
import {bus} from '@/bootstrap/bus'
import { Tote } from '@/types/WavePicking';
import {api} from "@/bootstrap/api";
import { AxiosResponse } from 'axios';
import {PickTask, usePickStore} from "@/stores/tasks/pick-tasks";
import { mapSmartOrders, mapWavePickingTask } from '@/utilities/mapWavePickOrder';
import { ApiSmartOrders, SmartOrderMapped } from '@/types/ApiSmartOrders';
import NotificationTypes from '@/enums/NotificationTypes';
import {useOrdersStore} from "@/stores/orders";
import {decodeToteScan} from "@/store/wavePicking/helpers";


export default /*#__PURE__*/_defineComponent({
  __name: 'WavePickStart',
  setup(__props, { expose }) {

const pickStore = usePickStore();
const orderStore = useOrdersStore();
const vuexStore = store;

const scannedTotes = ref<Tote[]>([]);
const error = ref<TranslateResult | null>(null);
const loadingOrders = ref(false);

bus.$on("barcodeScanned", handleItemScan);

onBeforeUnmount(() => {
  bus.$off("barcodeScanned", handleItemScan);
})

function handleItemScan(scanResult: string): void {
  error.value = null;
  const label = decodeToteScan(scanResult)
  if (!label) {
    error.value = i18n.t('wave_picking.unrecognised_tote');
    return;
  }
  let isRecognisedTote = false;

  // @todo: check database if this is a valid tote
  isRecognisedTote = true;
  if (!isRecognisedTote) {
    error.value = i18n.t('wave_picking.unrecognised_tote');
    return;
  }
  if(!totalOrders.value) {
    error.value = i18n.t('wave_picking.no_orders_for_wave_pick');
    return;
  }
  if(!!totalOrders.value && scannedTotes.value.length >= Number(totalOrders.value)) {
    error.value = i18n.t('wave_picking.more_totes_than_orders');
    return;
  }
  const toteIndex = scannedTotes.value.findIndex((t: Tote) => t.name === label.identifier);
  if (toteIndex >= 0) {
    error.value = i18n.t('wave_picking.duplicate_tote');
    return;
  }

  scannedTotes.value.push({
    id: null, // @todo update id with recognisedTote.id from DB
    name: label.identifier,
    userId: vuexStore.getters['core/getAuthUserId'],
    orderHeaderId: null,
  });
}

function removeTote(tote: string): void {
  if(scannedTotes.value.length === 0) {
    return
  }
  scannedTotes.value = scannedTotes.value.filter((t: Tote) => t.name !== tote)
}

function reset(): void {
  scannedTotes.value = []
  error.value = null
  loadingOrders.value = false
}

const appliedFilters = computed(() => {
  const storeFiltersAndSort = vuexStore.getters['orders/getFilterAndSortParams'];

  const appliedFilters = Object.fromEntries(Object.entries(storeFiltersAndSort).filter(([k, v]) => {
    // hub_id is added directly in the api call as a required param in startOrders() and its not really a filter in PP
    // filter_tab_group = "ready" | "pending"... removed from API call, as start smart pick in PPAPI removes pending already
    if(k === 'hub_id' || k === 'filter_tab_group') {
      return false
    }
    if(v === null || (Array.isArray(v) && v.length === 0)) {
      return false
    }
    return true
  }));

  // cast SKU from string in PP to number for PPAPI
  if(appliedFilters.filter_sku) {
    appliedFilters['filter_sku'] = (appliedFilters.filter_sku as string[]).map((id: string) => Number(id));
  }

  return appliedFilters;
})

async function startOrders(hub: string, numberOfOrders: number): Promise<ApiSmartOrders> {
  const result: AxiosResponse<ApiSmartOrders> = await api(
    'smartPickOrders',
    {
      headers: {
        Accept: 'application/vnd.api+json'
      },
      params: {
        'hub_id': hub,
        'quantity': numberOfOrders,
        ...appliedFilters.value
      },
      handleError: false
    }
  )
  return result.data;
}

async function startWavePick() {
  loadingOrders.value = true;
  const smartOrders = await startOrders(vuexStore.getters['core/getHubId'], scannedTotes.value.length)
  loadingOrders.value = false;
  if(smartOrders.data.length === 0) {
    store.dispatch('core/addNotification', {
      message: i18n.t('wave_picking.no_orders_from_api'),
      type: NotificationTypes.NOTIFICATION_TYPE_INFO,
      timeout: 3500,
    });
    return
  }

  if(smartOrders.data.length < scannedTotes.value.length) {
    scannedTotes.value.splice(smartOrders.data.length)
    store.dispatch('core/addNotification', {
      message: i18n.t('wave_picking.fewer_orders_from_api_than_totes'),
      type: NotificationTypes.NOTIFICATION_TYPE_INFO,
      timeout: 10000,
    });
  }

  const orderHeadersForTotes: SmartOrderMapped[] = mapSmartOrders(smartOrders)
  orderStore.setOrders(orderHeadersForTotes)

  scannedTotes.value = scannedTotes.value.map((tote: Tote, index: number): Tote => {
    return {...tote, orderHeaderId: orderHeadersForTotes[index].id}
  })

  const tasks: PickTask[] = [];
  orderHeadersForTotes.forEach((orderHeader) => {
    const foundTote = scannedTotes.value.find((tote: Tote) => tote.orderHeaderId === orderHeader.id);
    orderHeader.items.forEach(item => {
      tasks.push(mapWavePickingTask(item, foundTote))
    });
  })
  saveWavePickingTasks(tasks)
  reset()
  vuexStore.dispatch('core/redirect', { name: 'wave_pick_tasks' })
}

function saveWavePickingTasks(tasks: PickTask[]): void {
  pickStore.actions.setTotes(scannedTotes.value)
  pickStore.actions.setBaseTasks(tasks)
  pickStore.actions.setChallengesForAllTasks()
}

const totalOrders = computed(() => {
  return vuexStore.getters['orders/getTotal']
})

const canSubmit = computed(() => {
  // at least one scanned tote needed
  return scannedTotes.value.length > 0
})

const appliedFiltersCount = computed(() => {
  if(!appliedFilters.value) {
    return 0
  }
  const activeFilters = [
    appliedFilters.value.filter_huboo_box,
    appliedFilters.value.filter_work_group,
    appliedFilters.value.filter_carrier,
    appliedFilters.value.filter_sku_type,
    appliedFilters.value.filter_prime_orders,
  ];
  return activeFilters.filter((item) => !!item).length;
})

// used to expose params and methods in tests
expose({
  handleItemScan,
})


return { __sfc: true,pickStore, orderStore, vuexStore, scannedTotes, error, loadingOrders, handleItemScan, removeTote, reset, appliedFilters, startOrders, startWavePick, saveWavePickingTasks, totalOrders, canSubmit, appliedFiltersCount }
}

})